import { useEffect, useMemo, createRef } from 'react';
import * as WeatherLayers from 'weatherlayers-gl';
import { MapLayerSettings, MapLayerToggle } from '../../../api/mapping/types';
import { renderPalette } from '../utils';
import { BufferedFrameCache, VariableImageData } from '../caching';
import { MultiLayerTooltipControl } from '../MultiLayerTooltipControl';
import { useTheme } from '@app/theme/ThemeContext';

interface UseLegendTooltipControlsProps {
  layers: MapLayerSettings[];
  toggles: MapLayerToggle[];
  activeToggleIds: Set<string>;
  frameCache: BufferedFrameCache | null;
  displayFrame: number | null;
  locationPopoverVisible: boolean;
}

export function useLegendTooltipControls({
  layers,
  toggles,
  activeToggleIds,
  frameCache,
  displayFrame,
  locationPopoverVisible,
}: UseLegendTooltipControlsProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Determine visible layers (enabled + active toggle)
  const visibleLayers = useMemo(() => {
    return layers.filter((layer) => {
      if (!layer.enabled) {
        return false;
      }
      const containingToggles = toggles.filter((toggle) => toggle.layers.on?.includes(layer.id));
      if (containingToggles.length === 0) {
        return true;
      }
      return containingToggles.some((toggle) => activeToggleIds.has(toggle.title));
    });
  }, [layers, toggles, activeToggleIds]);

  // Only show legends for layers with legend.show === true
  const legendLayers = useMemo(() => visibleLayers.filter((l) => l.legend?.show), [visibleLayers]);

  // Create a LegendControl per legend layer
  const legendControlsById = useMemo(() => {
    const entries = legendLayers.map((l) => [l.id, new WeatherLayers.LegendControl()] as const);
    return Object.fromEntries(entries) as Record<string, WeatherLayers.LegendControl>;
  }, [legendLayers]);

  // Create refs for each legend container
  const legendControlRefs = useMemo(() => {
    return legendLayers.map((l) => ({ id: l.id, ref: createRef<HTMLDivElement>() }));
  }, [legendLayers]);

  const multiLayerTooltipControl = useMemo(() => new MultiLayerTooltipControl(), []);

  function applyTransparentLegendStyles(host: HTMLElement, dark: boolean) {
    // Ensure the immediate container is transparent
    host.style.setProperty('background', 'transparent', 'important');
    host.style.setProperty('box-shadow', 'none', 'important');
    host.style.setProperty('border', 'none', 'important');

    const walker = document.createTreeWalker(host, NodeFilter.SHOW_ELEMENT);
    let node = walker.nextNode() as HTMLElement | null;
    while (node) {
      const el = node as HTMLElement;
      if (el.tagName.toLowerCase() !== 'canvas') {
        el.style.setProperty('background', 'transparent', 'important');
        el.style.setProperty('box-shadow', 'none', 'important');
        el.style.setProperty('border', 'none', 'important');
        if (dark) {
          el.style.setProperty('color', '#ffffff', 'important');
        }
        if (dark && (el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'text')) {
          (el as any).style?.setProperty?.('fill', '#ffffff', 'important');
        }
      }
      node = walker.nextNode() as HTMLElement | null;
    }
  }

  // Mount/unmount legend controls into their refs
  useEffect(() => {
    if (locationPopoverVisible) {
      // remove all when popover is visible
      Object.values(legendControlsById).forEach((c) => c.remove());
      return;
    }
    const cleanups: (() => void)[] = [];
    legendControlRefs.forEach(({ id, ref }) => {
      const control = legendControlsById[id];
      if (ref.current && control) {
        control.prependTo(ref.current);
        // Post-mount style adjustments and observer to keep transparent when content updates
        const host = ref.current;
        const raf = requestAnimationFrame(() => {
          applyTransparentLegendStyles(host, isDark);
        });
        cleanups.push(() => cancelAnimationFrame(raf));

        const observer = new MutationObserver(() => {
          applyTransparentLegendStyles(host, isDark);
        });
        observer.observe(host, { attributes: true, childList: true, subtree: true });
        cleanups.push(() => observer.disconnect());
        cleanups.push(() => control.remove());
      }
    });
    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [legendControlRefs, legendControlsById, locationPopoverVisible, isDark]);

  // Update legend controls with current frame data
  useEffect(() => {
    if (frameCache == null || displayFrame == null || frameCache.frames[displayFrame] == null) {
      return;
    }
    const frame = frameCache.frames[displayFrame];
    const varData = frame.variableData;
    if (varData == null) {
      return;
    }

    legendLayers.forEach((layer) => {
      const control = legendControlsById[layer.id];
      if (!control) {
        return;
      }
      const baseConfig: any = {
        title: layer.legend?.title ?? layer.id,
        unitFormat: layer.legend?.unitFormat ?? { unit: '' },
      };
      if ((layer as any).variable && layer.kind === 'RASTER') {
        const variableId = (layer as any).variable as string;
        const vd = varData[variableId];
        if (vd) {
          const [minScale, maxScale] = vd.imageUnscale;
          baseConfig.palette = renderPalette(
            (layer as any).colorScaleMin ?? minScale,
            (layer as any).colorScaleMax ?? maxScale,
            (layer as any).palette,
            1,
          );
        }
      }
      control.updateConfig(baseConfig);
    });
  }, [legendLayers, legendControlsById, displayFrame, frameCache]);

  // Update multi-layer tooltip control based on enabled layers with tooltip.show = true
  useEffect(() => {
    if (frameCache == null || displayFrame == null || frameCache.frames[displayFrame] == null) {
      multiLayerTooltipControl.updateConfig({ layers: [] });
      return;
    }

    const frame = frameCache.frames[displayFrame];
    const varData = frame.variableData ?? {};

    // Find all enabled layers that should show tooltips and have data
    const tooltipLayers = layers
      .filter((layer) => layer.enabled && layer.tooltip?.show)
      .map((layer) => {
        const data: VariableImageData | undefined = (layer as any).variable
          ? varData[(layer as any).variable]
          : undefined;
        return {
          id: layer.id,
          title: layer.tooltip?.title ?? layer.id,
          unitFormat:
            layer.kind === 'RASTER' ||
            layer.kind === 'HIGHLOW' ||
            layer.kind === 'PARTICLE' ||
            layer.kind === 'CONTOUR'
              ? layer.legend?.unitFormat
              : undefined,
          variable: (layer as any).variable,
          kind: layer.kind,
          data,
        };
      })
      .filter((l) => l.data != null);

    multiLayerTooltipControl.updateConfig({
      layers: tooltipLayers as any,
      followCursor: true,
      followCursorOffset: 16,
      followCursorPlacement: 'BOTTOM',
    });
  }, [layers, multiLayerTooltipControl, displayFrame, frameCache]);

  return {
    tooltipControl: multiLayerTooltipControl,
    legendControlRefs,
  };
}
